import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  FileText,
  Image,
  Mail,
  MessageSquareQuote,
  Newspaper,
  Plus,
  Upload,
  Users,
} from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { AdminCard, AdminSpinner } from "@/components/admin/AdminUi";

const CARD_STYLES = [
  { key: "published_pages", label: "Published Pages", Icon: FileText, link: "/admin/pages", cta: "Manage Pages", color: "bg-emerald-50 text-bka-emerald" },
  { key: "new_nominations", label: "New Nominations", Icon: Users, link: "/admin/emerald/nominations", cta: "View Nominations", color: "bg-amber-50 text-bka-gold" },
  { key: "new_enquiries", label: "New Enquiries", Icon: Mail, link: "/admin/enquiries", cta: "View Enquiries", color: "bg-teal-50 text-bka-teal" },
  { key: "pending_testimonials", label: "Testimonials Awaiting Review", Icon: MessageSquareQuote, link: "/admin/testimonials", cta: "Review Now", color: "bg-rose-50 text-rose-600" },
];

const QUICK_ACTIONS = [
  { label: "Add New Page", href: "/admin/pages/new", Icon: Plus, color: "bg-emerald-50 text-bka-emerald" },
  { label: "Create Blog Post", href: "/admin/posts/new", Icon: Newspaper, color: "bg-blue-50 text-blue-600" },
  { label: "View Nominations", href: "/admin/emerald/nominations", Icon: Users, color: "bg-amber-50 text-bka-gold" },
  { label: "Upload Media", href: "/admin/media", Icon: Upload, color: "bg-violet-50 text-violet-600" },
  { label: "View Enquiries", href: "/admin/enquiries", Icon: Mail, color: "bg-teal-50 text-bka-teal" },
  { label: "Media Library", href: "/admin/media", Icon: Image, color: "bg-slate-100 text-slate-600" },
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <AdminSpinner label="Loading your dashboard" />;
  if (data === false)
    return <p className="text-sm text-red-600">We couldn't load the dashboard. Please refresh the page.</p>;

  const { cards, activity, health, health_score: score } = data;
  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div data-testid="cms-dashboard">
      <Seo title="Dashboard | Bahamas Kidney Association CMS" noindex />

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-bka-navy lg:text-3xl">Dashboard</h1>
          <p className="mt-1.5 text-sm text-slate-500">Manage content, engagement and make a bigger impact.</p>
        </div>
        <p className="text-sm text-slate-400">{today}</p>
      </div>

      {/* STAT CARDS */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {CARD_STYLES.map(({ key, label, Icon, link, cta, color }) => (
          <AdminCard key={key} testId={`cms-stat-card-${key}`} className="p-5">
            <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${color}`}>
              <Icon className="h-5 w-5" />
            </span>
            <p className="mt-4 font-display text-3xl font-bold text-bka-navy">{cards[key]}</p>
            <p className="mt-1 text-sm font-semibold text-slate-500">{label}</p>
            <Link
              to={link}
              data-testid={`cms-stat-link-${key}`}
              className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-bka-emerald hover:gap-2 transition-all"
            >
              {cta} <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </AdminCard>
        ))}
      </div>

      {/* BANNER */}
      <div className="relative mt-5 overflow-hidden rounded-2xl bg-bka-navy">
        <img
          src="https://images.pexels.com/photos/913100/pexels-photo-913100.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{ background: "linear-gradient(95deg, rgba(11,53,84,0.96) 0%, rgba(11,53,84,0.7) 55%, rgba(0,140,114,0.35) 100%)" }}
        />
        <div className="relative flex flex-col gap-6 p-7 lg:flex-row lg:items-center lg:justify-between lg:p-10">
          <div>
            <h2 className="max-w-md font-display text-2xl font-bold leading-tight text-white lg:text-3xl">
              Together for Healthier Tomorrows
            </h2>
            <p className="mt-3 text-sm font-semibold text-white/70">Educate. Support. Empower. Save Lives.</p>
            <div className="mt-5 h-[3px] w-14 rounded-full bg-bka-gold" />
          </div>
          <p className="max-w-xs font-display text-lg italic leading-snug text-white/90">
            "A healthier Bahamas is within our reach."
          </p>
        </div>
      </div>

      {/* THREE COLUMNS */}
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <AdminCard testId="cms-recent-activity">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="font-display text-base font-bold text-bka-navy">Recent Activity</h2>
            <Link to="/admin/audit-log" className="text-xs font-bold text-bka-emerald">
              View All
            </Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {activity.length === 0 && <li className="px-5 py-8 text-sm text-slate-500">No activity yet.</li>}
            {activity.map((a) => (
              <li key={a.id} className="flex items-start gap-3 px-5 py-4">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-500">
                  {(a.user_email || "?")[0].toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-bka-navy">
                    {a.action.replace(/[._]/g, " ")}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    {a.metadata?.title ||
                      a.metadata?.nominee ||
                      a.metadata?.email ||
                      a.metadata?.filename ||
                      a.user_email ||
                      "—"}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-slate-400">{timeAgo(a.created_at)}</span>
              </li>
            ))}
          </ul>
        </AdminCard>

        <AdminCard testId="cms-quick-actions">
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="font-display text-base font-bold text-bka-navy">Quick Actions</h2>
          </div>
          <div className="space-y-2 p-4">
            {QUICK_ACTIONS.map(({ label, href, Icon, color }) => (
              <Link
                key={label}
                to={href}
                data-testid={`cms-quick-action-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 transition-colors hover:border-bka-emerald hover:bg-emerald-50/40"
              >
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${color}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="flex-1 text-sm font-bold text-bka-navy">{label}</span>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </Link>
            ))}
          </div>
        </AdminCard>

        <AdminCard testId="cms-website-health">
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="font-display text-base font-bold text-bka-navy">Website Health</h2>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-5">
              <div
                className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-full"
                style={{
                  background: `conic-gradient(var(--bka-emerald) ${score * 3.6}deg, #e2e8f0 ${score * 3.6}deg)`,
                }}
                role="img"
                aria-label={`Health score ${score} percent`}
              >
                <span className="flex h-[76px] w-[76px] items-center justify-center rounded-full bg-white font-display text-lg font-bold text-bka-navy">
                  {score}%
                </span>
              </div>
              <p className="text-sm font-bold text-bka-navy">
                {score === 100 ? "All Systems Operational" : "Action Needed"}
              </p>
            </div>

            <ul className="mt-5 space-y-2.5">
              {health.map((h) => (
                <li key={h.label} className="flex items-start gap-2.5 text-sm">
                  {h.ok ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bka-emerald" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-bka-gold" />
                  )}
                  <span className="text-slate-600">{h.label}</span>
                </li>
              ))}
            </ul>

            {health
              .filter((h) => h.warning)
              .map((h) => (
                <div
                  key={h.label}
                  data-testid="cms-health-warning"
                  className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
                >
                  <p className="text-xs font-bold text-amber-900">{h.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-amber-800">{h.warning}</p>
                  {h.label === "Donations Configured" && (
                    <Link
                      to="/admin/donations"
                      data-testid="cms-health-donation-link"
                      className="mt-2 inline-block text-xs font-bold text-amber-900 underline"
                    >
                      Open Donation Settings
                    </Link>
                  )}
                </div>
              ))}

            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-bold text-emerald-900">Secure donations</p>
              <p className="mt-1 text-xs leading-relaxed text-emerald-800">
                Donations are processed on an external hosted payment page. No card data is ever stored in
                this CMS.
              </p>
            </div>
          </div>
        </AdminCard>
      </div>
    </div>
  );
}
